import React, { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import BeforeAfterSlider from './BeforeAfterSlider';

const ModalHeader = ({ category, onClose }) => (
  <>
    <button 
      onClick={onClose} 
      className="fixed top-8 right-8 z-50 w-12 h-12 flex items-center justify-center bg-black text-white hover:bg-gray-800 transition-colors"
    >
      <X size={24} />
    </button>
    <div className="border-b border-black pb-8 mb-12">
      <div className="flex items-baseline justify-between">
        <p className="text-xs tracking-[0.3em] uppercase text-gray-600" style={{ fontFamily: 'Archivo, sans-serif' }}>
          {category}
        </p>
      </div>
    </div>
  </>
);

const ModalTitle = ({ title }) => (
  <div className="mb-20">
    <h1 className="text-6xl md:text-8xl lg:text-9xl font-bold leading-none mb-6 tracking-tight" style={{ fontFamily: 'Archivo, sans-serif' }}>
      {title}
    </h1>
  </div>
);

const ProjectDescription = ({ description }) => {
  const firstSentence = description.split('.')[0] + '.';
  const restOfText = description.substring(firstSentence.length).trim();

  return (
    <div className="grid md:grid-cols-2 gap-12 mb-20 max-w-6xl">
      {/* First Column with Drop Cap - BOLD */}
      <div>
        <p className="text-base leading-relaxed text-gray-800 font-bold" style={{ fontFamily: 'Archivo, sans-serif' }}>
          <span className="float-left text-7xl font-bold leading-none mr-3" style={{ fontFamily: 'Archivo, sans-serif', lineHeight: '0.8' }}>
            {firstSentence.charAt(0)}
          </span>
          {firstSentence.substring(1)}
        </p>
      </div>
      
      {/* Second Column - Regular Text */}
      <div>
        <p className="text-base leading-relaxed text-gray-800" style={{ fontFamily: 'Archivo, sans-serif' }}>
          {restOfText}
        </p>
        
        {/* Pull Quote / Highlight */}
        <div className="mt-8 pt-8 border-t border-black">
          <p className="text-sm tracking-widest uppercase text-gray-600" style={{ fontFamily: 'Archivo, sans-serif' }}>
            CREATIVE DIRECTION / DESIGN
          </p>
          <p className="text-lg font-bold mt-2" style={{ fontFamily: 'Archivo, sans-serif' }}>
            Jana Janke
          </p>
        </div>
      </div>
    </div>
  );
};

const SliderGallery = ({ sliders }) => (
  <>
    {sliders.map((slider, index) => (
      <div key={`slider-${index}`}>
        <p className="text-xs tracking-widest text-gray-500 uppercase mb-6" style={{ fontFamily: 'Archivo, sans-serif' }}>
          Fig. {String(index + 1).padStart(2, '0')}
        </p>
        <BeforeAfterSlider
          beforeImage={slider.before}
          afterImage={slider.after}
          title={slider.title}
          beforeLabel="C4D WIREFRAME"
          afterLabel="FINAL RENDER"
        />
      </div>
    ))}
  </>
);

const ImageGallery = ({ images, projectTitle, projectId }) => (
  <>
    {images.map((image, index) => (
      <div key={`${projectId}-img-${index}`} className="w-full">
        <div className="w-full">
          <img 
            src={image} 
            alt={`${projectTitle} - ${index + 1}`} 
            loading="lazy"
            className="w-full h-auto" 
          />
        </div>
        
        <div className="flex items-baseline justify-between mt-4 px-2">
          <p className="text-xs tracking-widest text-gray-500 uppercase" style={{ fontFamily: 'Archivo, sans-serif' }}>
            Fig. {String(index + 1).padStart(2, '0')}
          </p>
          <p className="text-xs text-gray-400" style={{ fontFamily: 'Archivo, sans-serif' }}>
            {projectTitle}
          </p>
        </div>
      </div>
    ))}
  </>
);

const PlaceholderGallery = () => (
  <div className="w-full aspect-video bg-gray-100 border border-black flex items-center justify-center">
    <p className="text-gray-400 tracking-widest text-xs uppercase" style={{ fontFamily: 'Archivo, sans-serif' }}>
      Images Coming Soon
    </p>
  </div>
);

const ModalFooter = ({ onClose }) => (
  <div className="mt-32 pt-12 border-t-2 border-black">
    <div className="flex items-center justify-between">
      <button 
        onClick={onClose} 
        className="text-xs tracking-[0.3em] uppercase hover:opacity-60 transition-opacity" 
        style={{ fontFamily: 'Archivo, sans-serif' }}
      >
        ← Back to Selected Work
      </button>
      <p className="text-xs text-gray-400" style={{ fontFamily: 'Archivo, sans-serif' }}>
        © 2024 Jana Janke
      </p>
    </div>
  </div>
);

const ProjectModal = ({ project, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const handleEsc = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [handleEsc]);

  const renderGallery = () => {
    if (project.id === 9 && project.sliders) {
      return <SliderGallery sliders={project.sliders} />;
    }
    if (project.images && project.images.length > 0) {
      return <ImageGallery images={project.images} projectTitle={project.title} projectId={project.id} />;
    }
    return <PlaceholderGallery />;
  };

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-y-auto animate-in fade-in duration-300" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="container mx-auto px-6 py-16 md:py-24 max-w-7xl">
        <ModalHeader category={project.category} onClose={onClose} />
        <ModalTitle title={project.title} />
        <ProjectDescription description={project.description} />
        
        <div className="space-y-24">
          {renderGallery()}
        </div>

        <ModalFooter onClose={onClose} />
      </div>
    </div>
  );
};

export default ProjectModal;